import { useCallback } from "react";

import {
  STT_ERROR_CODE_INVALID_AUDIO,
  STT_ERROR_CODE_NETWORK,
  STT_ERROR_CODE_TRANSCRIPTION
} from "./SpeechToTextRecorderError";
import { DEFAULT_RECORDING_CONTENT_TYPE } from "./useBrowserAudioRecorder";
import {
  MIN_RECORDING_DURATION_MS,
  STT_RECORDER_STATE_IDLE,
  STT_RECORDER_STATE_TRANSCRIBING
} from "./useSpeechToTextRecorder";
import nowInMs from "../Utils/nowInMs";

const transcriptionErrorCodeFor = error => {
  if (error.message === STT_ERROR_CODE_NETWORK) {
    return STT_ERROR_CODE_NETWORK;
  }

  return STT_ERROR_CODE_INVALID_AUDIO;
};

const normalizedTranscription = transcription => {
  return (transcription || "").trim();
};

// Finalizes recorded chunks and orchestrates the transcription request lifecycle.
const useSpeechToTextRecorderProcessing = ({
  clearDiscardRecordingFlag,
  consumeRecordedData,
  hasAudioSignalRef,
  onError,
  onRecordingReady,
  onResult,
  recordingHandled,
  recordingStartedAtRef,
  requestTranscription,
  setRecorderState,
  setRecordingHandled,
  shouldDiscardRecording,
  stopMediaStreamTracks,
  stopSignalMonitor
}) => {
  const resetToIdle = useCallback(() => {
    stopMediaStreamTracks();
    setRecorderState(STT_RECORDER_STATE_IDLE);
  }, [setRecorderState, stopMediaStreamTracks]);

  const shouldSkipTranscription = useCallback(
    ({
      hasAudioSignal,
      hasRecordedChunks,
      isRecordingTooShort,
      shouldDiscardRecordingNow
    }) => {
      if (shouldDiscardRecordingNow) {
        clearDiscardRecordingFlag();
        resetToIdle();
        return true;
      }

      if (isRecordingTooShort) {
        resetToIdle();
        return true;
      }

      if (!hasRecordedChunks) {
        onError?.(STT_ERROR_CODE_INVALID_AUDIO);
        resetToIdle();
        return true;
      }

      if (!hasAudioSignal) {
        // Providers may hallucinate text for silent audio,
        // so we skip the request entirely.
        resetToIdle();
        return true;
      }

      return false;
    },
    [clearDiscardRecordingFlag, onError, resetToIdle]
  );

  const processTranscription = useCallback(
    async ({ audioBlob, audioContentType }) => {
      setRecorderState(STT_RECORDER_STATE_TRANSCRIBING);

      try {
        const result = await requestTranscription(audioBlob, audioContentType);

        if (!result?.success) {
          onError?.(STT_ERROR_CODE_TRANSCRIPTION);
          return;
        }

        // GraphQL may return nil/blank depending on provider output.
        // Normalize to "no result".
        const transcription = normalizedTranscription(result?.transcription);
        if (!transcription) return;

        onResult?.(transcription);

        // Callers that need to replay the take (ex: the oral interview speaker test)
        // receive an object URL for the recorded audio. Only built on demand so other
        // recorder users allocate nothing.
        onRecordingReady?.(URL.createObjectURL(audioBlob));
      } catch (error) {
        onError?.(transcriptionErrorCodeFor(error));
      } finally {
        // Always release microphone immediately after processing.
        resetToIdle();
      }
    },
    [
      onError,
      onRecordingReady,
      onResult,
      requestTranscription,
      resetToIdle,
      setRecorderState
    ]
  );

  return useCallback(async () => {
    // `onstop` can be triggered from multiple paths (send/delete/unmount).
    // Guard idempotency.
    if (recordingHandled()) return;
    setRecordingHandled(true);

    const { mediaRecorder, recordingChunks } = consumeRecordedData();
    const hasAudioSignal = hasAudioSignalRef.current;
    const recordingDurationMs = Math.max(
      0,
      nowInMs() - (recordingStartedAtRef.current || 0)
    );
    const shouldDiscardRecordingNow = shouldDiscardRecording();
    const isRecordingTooShort = recordingDurationMs < MIN_RECORDING_DURATION_MS;
    const hasRecordedChunks = recordingChunks.length > 0;

    recordingStartedAtRef.current = null;
    hasAudioSignalRef.current = false;

    stopSignalMonitor();

    if (
      shouldSkipTranscription({
        hasAudioSignal,
        hasRecordedChunks,
        isRecordingTooShort,
        shouldDiscardRecordingNow
      })
    ) {
      return;
    }

    const audioContentType = mediaRecorder?.mimeType || DEFAULT_RECORDING_CONTENT_TYPE;
    const audioBlob = new Blob(recordingChunks, {
      type: audioContentType
    });

    await processTranscription({ audioBlob, audioContentType });
  }, [
    consumeRecordedData,
    hasAudioSignalRef,
    processTranscription,
    recordingHandled,
    recordingStartedAtRef,
    setRecordingHandled,
    shouldDiscardRecording,
    shouldSkipTranscription,
    stopSignalMonitor
  ]);
};

export default useSpeechToTextRecorderProcessing;
